import FactService, { type HomeopathyFact } from '../services/factService';
import { logger } from '../utils/logger';

export interface FactNotificationResult {
  shouldShow: boolean;
  fact: HomeopathyFact | null;
  timeUntilNext: number;
  isFirstTime: boolean;
}

class FactNotificationSystem {
  private static instance: FactNotificationSystem;
  private readonly TWENTY_FOUR_HOURS = 24 * 60 * 60 * 1000;
  private factService: FactService;

  private constructor() {
    this.factService = FactService.getInstance();
  }

  public static getInstance(): FactNotificationSystem {
    if (!FactNotificationSystem.instance) {
      FactNotificationSystem.instance = new FactNotificationSystem();
    }
    return FactNotificationSystem.instance;
  }

  public async checkForNotification(userId: string): Promise<FactNotificationResult> {
    logger.sensitive('Verificando curiosidade', { userId });

    const fact = await this.factService.selectFactForUser(userId);

    if (!fact) {
      console.log('⚠️ [FactNotification] Nenhuma curiosidade disponível');
      return {
        shouldShow: false,
        fact: null,
        timeUntilNext: 0,
        isFirstTime: false,
      };
    }

    console.log('✅ [FactNotification] Curiosidade selecionada (ID omitido por segurança)');

    return {
      shouldShow: true,
      fact,
      timeUntilNext: 0,
      isFirstTime: false,
    };
  }

  public async markFactAsReadWithPoints(factId: string, userId: string): Promise<boolean> {
    const result = await this.factService.markFactAsRead(factId, userId, true);

    if (!result.success) {
      if (result.reason === 'already_marked_today') {
        console.warn('⚠️ [FactNotification] Curiosidade já pontuada hoje:', factId);
      } else {
        console.error('❌ [FactNotification] Erro ao pontuar curiosidade:', result.message);
      }
      return false;
    }

    console.log('✅ [FactNotification] Pontos registrados via trigger do banco');
    return true;
  }

  public async markFactAsRead(factId: string, userId: string): Promise<void> {
    const result = await this.factService.markFactAsRead(factId, userId, false);

    if (!result.success) {
      if (result.reason === 'already_marked_today') {
        console.warn('⚠️ [FactNotification] Curiosidade já registrada hoje (sem pontos):', factId);
      } else {
        console.error('❌ [FactNotification] Falha ao registrar curiosidade (sem pontos):', result.message);
      }
    }
  }

  public async hasEarnedPointsInLast24Hours(userId: string): Promise<boolean> {
    try {
      const { supabase } = await import('../lib/supabase');
      const since = new Date(Date.now() - this.TWENTY_FOUR_HOURS).toISOString();
      const { data, error } = await supabase
        .from('user_fact_reads')
        .select('fact_id')
        .eq('user_id', userId)
        .eq('points_awarded', true)
        .gte('read_at', since)
        .limit(1);

      return !error && Array.isArray(data) && data.length > 0;
    } catch (error) {
      console.error('❌ [FactNotification] Erro ao verificar pontos:', error);
      return false;
    }
  }

  public async getFactNotificationStatus(userId: string): Promise<{
    hasUnreadFacts: boolean;
    currentFactHasEarnedPoints: boolean;
    currentFactId: string | null;
    canShowNow: boolean;
  }> {
    const now = new Date();
    const dateInBRT = new Date(now.getTime() - 3 * 60 * 60 * 1000);
    const todayDate = new Date(dateInBRT.getUTCFullYear(), dateInBRT.getUTCMonth(), dateInBRT.getUTCDate())
      .toISOString()
      .split('T')[0];
    const todayReadFactId = await this.factService.getTodayReadFact(userId, todayDate);
    const hasEarnedPoints = await this.hasEarnedPointsInLast24Hours(userId);

    if (todayReadFactId && hasEarnedPoints) {
      return {
        hasUnreadFacts: true,
        currentFactHasEarnedPoints: true,
        currentFactId: todayReadFactId,
        canShowNow: false,
      };
    }

    return {
      hasUnreadFacts: true,
      currentFactHasEarnedPoints: false,
      currentFactId: null,
      canShowNow: true,
    };
  }

  public async getLastFactShown(userId: string): Promise<HomeopathyFact | null> {
    const now = new Date();
    const dateInBRT = new Date(now.getTime() - 3 * 60 * 60 * 1000);
    const todayDate = new Date(dateInBRT.getUTCFullYear(), dateInBRT.getUTCMonth(), dateInBRT.getUTCDate())
      .toISOString()
      .split('T')[0];
    const todayReadFactId = await this.factService.getTodayReadFact(userId, todayDate);

    if (todayReadFactId) {
      return this.factService.getFactById(todayReadFactId);
    }

    const readIds = await this.factService.getAllReadFactIds(userId);
    if (readIds.length === 0) {
      return null;
    }

    return this.factService.getFactById(readIds[0]);
  }

  public async forceNextNotification(userId: string): Promise<FactNotificationResult> {
    return this.checkForNotification(userId);
  }

  public async getReadFactsHistory(userId: string): Promise<string[]> {
    return this.factService.getAllReadFactIds(userId);
  }

  public async resetNotificationState(userId: string): Promise<void> {
    try {
      localStorage.removeItem(`magistral_fact_notifications_${userId}`);
    } catch (error) {
      console.warn('⚠️ [FactNotification] Falha ao limpar cache antigo:', error);
    }
  }
}

export { FactNotificationSystem };
export default FactNotificationSystem;